import React, { useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  MenuItem,
  Paper,
  Checkbox,
  FormGroup,
  Chip,
  Alert,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  CreateBossBabyTaskDto,
  UpdateBossBabyTaskDto,
  BossBabyTaskDto,
} from '@/dto/BossBabyDto';
import { useBossBabyUtils } from '@/hooks/useBossBaby';

interface TaskFormProps {
  initialData?: BossBabyTaskDto;
  onSubmit: (data: CreateBossBabyTaskDto | UpdateBossBabyTaskDto) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

type FormData = {
  taskName: string;
  description: string;
  category: string;
  tools: string[];
  priorityLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  executionFrequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  llmPrompt: string; // New field for LLM Prompt
  taskConfig: string;
  exampleMessage: string;
  exampleDescription: string;
  isActive: boolean;
};

export const TaskForm: React.FC<TaskFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false
}) => {
  const { activeTools, fetchActiveTools, validateTaskConfig } = useBossBabyUtils();

  // Helper to extract prompt and config
  const getInitialConfig = () => {
    if (!initialData?.taskConfig) {
      return {
        prompt: '',
        config: `{
  "maxRetries": 3,
  "timeout": 60000,
  "priority": 10,
  "autoExecute": true,
  "requiresConfirmation": false
}`
      };
    }

    const config = { ...initialData.taskConfig } as any;
    const prompt = config.llmPrompt || '';

    // Remove prompt from config for the JSON editor to avoid duplication
    const configWithoutPrompt = { ...config };
    delete configWithoutPrompt.llmPrompt;

    return {
      prompt,
      config: JSON.stringify(configWithoutPrompt, null, 2)
    };
  };

  const initialConfig = getInitialConfig();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    defaultValues: {
      taskName: initialData?.taskName || '',
      description: initialData?.description || '',
      category: initialData?.category || 'boss_baby',
      tools: initialData?.tools || [],
      priorityLevel: initialData?.priorityLevel || 'MEDIUM',
      executionFrequency: initialData?.executionFrequency || 'DAILY',
      llmPrompt: initialConfig.prompt,
      taskConfig: initialConfig.config,
      exampleMessage: initialData?.exampleMessage || '',
      exampleDescription: initialData?.exampleDescription || '',
      isActive: initialData?.isActive ?? true,
    }
  });

  useEffect(() => {
    fetchActiveTools();
  }, [fetchActiveTools]);

  const defaultProps = {
    size: "small" as const,
    fullWidth: true,
  };

  const selectedTools = watch('tools');

  const handleToolToggle = (toolName: string) => {
    const currentTools = selectedTools;
    const newTools = currentTools.includes(toolName)
      ? currentTools.filter(t => t !== toolName)
      : [...currentTools, toolName];
    setValue('tools', newTools);
  };

  const handleFormSubmit = (data: FormData) => {
    try {
      const parsedConfig = JSON.parse(data.taskConfig);

      // Merge LLM Prompt back into config
      const finalConfig = {
        ...parsedConfig,
        llmPrompt: data.llmPrompt.trim()
      };

      const submitData = {
        ...data,
        taskConfig: finalConfig,
        description: data.description.trim(),
        exampleMessage: data.exampleMessage.trim() || undefined,
        exampleDescription: data.exampleDescription.trim() || undefined,
      };

      if (isEdit) {
        const updateData: UpdateBossBabyTaskDto = {
          description: submitData.description,
          tools: submitData.tools,
          priorityLevel: submitData.priorityLevel,
          executionFrequency: submitData.executionFrequency,
          taskConfig: submitData.taskConfig,
          exampleMessage: submitData.exampleMessage,
          exampleDescription: submitData.exampleDescription,
          isActive: submitData.isActive,
        };
        onSubmit(updateData);
      } else {
        const createData: CreateBossBabyTaskDto = {
          taskName: submitData.taskName,
          description: submitData.description,
          category: submitData.category,
          tools: submitData.tools,
          priorityLevel: submitData.priorityLevel,
          executionFrequency: submitData.executionFrequency,
          taskConfig: submitData.taskConfig,
          exampleMessage: submitData.exampleMessage,
          exampleDescription: submitData.exampleDescription,
        };
        onSubmit(createData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const validateConfig = (value: string) => {
    if (!value.trim()) return 'Task config is required';
    if (!validateTaskConfig(value)) return 'Invalid JSON format';
    return true;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {isEdit ? 'Edit Boss Baby Task' : 'Create Boss Baby Task'}
        </Typography>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
          </Grid>

          {/* Task Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Task Name</FormLabel>
              <Controller
                name="taskName"
                control={control}
                rules={{
                  required: 'Task name is required',
                  maxLength: {
                    value: 255,
                    message: 'Maximum 255 characters'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    disabled={isEdit}
                    error={!!errors.taskName}
                    helperText={errors.taskName?.message}
                    placeholder="e.g., BossBaby.Schedule.Create"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Category */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Category</FormLabel>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    select
                    error={!!errors.category}
                    helperText={errors.category?.message}
                  >
                    <MenuItem value="boss_baby">Boss Baby</MenuItem>
                    <MenuItem value="automation">Automation</MenuItem>
                    <MenuItem value="notification">Notification</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel required>Description</FormLabel>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: 'Description is required',
                  maxLength: {
                    value: 1000,
                    message: 'Maximum 1000 characters'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    multiline
                    rows={2}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Describe what this task does"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* LLM Prompt - The Core Instruction */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                🤖 LLM Prompt (The Brain)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This is the instruction that tells the AI what to do. Be specific about how to use the tools.
              </Typography>
            </Box>

            <FormControl fullWidth>
              <Controller
                name="llmPrompt"
                control={control}
                rules={{
                  required: 'LLM Prompt is required. This is the core instruction for the AI.',
                  minLength: {
                    value: 10,
                    message: 'Prompt should be at least 10 characters long'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    multiline
                    rows={6}
                    error={!!errors.llmPrompt}
                    helperText={errors.llmPrompt?.message || "e.g., 'When the user asks to schedule a meeting, extract the 5W1H info and use the ScheduleManagementTool...'"}
                    placeholder="Enter the detailed instruction for the AI here..."
                    sx={{
                      bgcolor: 'action.hover',
                      '& .MuiInputBase-root': { fontSize: '1.05rem' }
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Tools Selection */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              🛠️ Tools (The Hands)
            </Typography>
            <FormControl fullWidth>
              <FormLabel required>Select Tools</FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select the tools that the AI is allowed to use for this task.
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, maxHeight: 200, overflowY: 'auto' }}>
                <FormGroup>
                  {activeTools.map((tool) => (
                    <FormControlLabel
                      key={tool.toolId}
                      control={
                        <Checkbox
                          checked={selectedTools.includes(tool.toolName)}
                          onChange={() => handleToolToggle(tool.toolName)}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{tool.toolName}</Typography>
                          <Chip
                            label={tool.priorityLevel}
                            size="small"
                            color={tool.priorityLevel === 'HIGH' ? 'error' : tool.priorityLevel === 'MEDIUM' ? 'warning' : 'info'}
                          />
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
              {selectedTools.length === 0 && (
                <Typography variant="caption" color="error">
                  At least one tool must be selected
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Configuration */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              ⚙️ Configuration
            </Typography>
          </Grid>

          {/* Priority Level */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Priority Level</FormLabel>
              <Controller
                name="priorityLevel"
                control={control}
                rules={{ required: 'Priority level is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    select
                    error={!!errors.priorityLevel}
                    helperText={errors.priorityLevel?.message}
                  >
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          {/* Execution Frequency */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <FormLabel required>Execution Frequency</FormLabel>
              <Controller
                name="executionFrequency"
                control={control}
                rules={{ required: 'Execution frequency is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    select
                    error={!!errors.executionFrequency}
                    helperText={errors.executionFrequency?.message}
                  >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          {/* Task Config */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel required>Advanced Config (JSON)</FormLabel>
              <Controller
                name="taskConfig"
                control={control}
                rules={{
                  required: 'Task config is required',
                  validate: validateConfig
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    multiline
                    rows={6}
                    error={!!errors.taskConfig}
                    helperText={errors.taskConfig?.message || 'Additional configuration parameters (timeout, retries, etc.)'}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Examples */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Examples
            </Typography>
          </Grid>

          {/* Example Message */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel>Example Message</FormLabel>
              <Controller
                name="exampleMessage"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    placeholder="e.g., Add a meeting for tomorrow at 3 PM"
                    helperText="An example user message that would trigger this task"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Example Description */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel>Example Description</FormLabel>
              <Controller
                name="exampleDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...defaultProps}
                    multiline
                    rows={2}
                    placeholder="e.g., Creates a new schedule entry when user requests to add an appointment"
                    helperText="Describe how this task would be used in practice"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Status */}
          {isEdit && (
            <>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Status
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="Active"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}

          {/* Actions */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 10 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || selectedTools.length === 0}
              >
                {isSubmitting ? 'Saving...' : (isEdit ? 'Update Task' : 'Create Task')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};